import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion'; // Import framer-motion

function Donate() {
  const { t } = useTranslation();

  // Define the animation variants for the Donation Options Section
  const donationOptionsVariants = {
    hidden: { opacity: 0, y: 50 }, // Start 50px below and invisible
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.8, 
        ease: 'easeOut',
        staggerChildren: 0.2 // Stagger the animation of child elements (donation cards)
      } 
    },
  };

  // Animation variants for individual donation option cards
  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  // Define the animation variants for the Bank Transfer Section
  const bankTransferVariants = {
    hidden: { opacity: 0, y: 50 }, // Start 50px below and invisible
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }, // Fade in and slide up
  };

  return (
    <div>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{t('donate.seoTitle')}</title>
        <meta name="description" content={t('donate.seoDescription')} />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center h-[400px] flex items-center justify-center text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('/assets/images/hero-bg.jpg')`,
        }}
      >
        <div className="text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('donate.heroTitle')}
          </h1>
          <p className="text-lg md:text-xl">
            {t('donate.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Donation Options Section */}
      <section className="bg-secondary py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible" // Animate when the element comes into view
            viewport={{ once: true, amount: 0.2 }} // Trigger animation once when 20% of the element is visible
            variants={donationOptionsVariants}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-textPrimary text-center mb-6">
              {t('donate.sectionTitle')}
            </h2>
            <p className="text-lg text-textPrimary text-center mb-12">
              {t('donate.sectionSubtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div
                variants={cardVariants}
                className="bg-white shadow-md rounded-lg p-6 transform hover:scale-105 transition-transform duration-300"
              >
                <h3 className="text-xl font-semibold text-textPrimary mb-4">
                  {t('donate.oneTimeTitle')}
                </h3>
                <p className="text-textPrimary mb-4">
                  {t('donate.oneTimeDescription')}
                </p>
                <a
                  href="mailto:info@notashamedofthegospelministry.org.com?subject=One-Time Donation Inquiry"
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  {t('donate.oneTimeButton')}
                </a>
              </motion.div>
              <motion.div
                variants={cardVariants}
                className="bg-white shadow-md rounded-lg p-6 transform hover:scale-105 transition-transform duration-300"
              >
                <h3 className="text-xl font-semibold text-textPrimary mb-4">
                  {t('donate.monthlyTitle')}
                </h3>
                <p className="text-textPrimary mb-4">
                  {t('donate.monthlyDescription')}
                </p>
                <a
                  href="mailto:info@notashamedofthegospelministry.org.com?subject=Monthly Giving Inquiry"
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  {t('donate.monthlyButton')}
                </a>
              </motion.div>
              <motion.div
                variants={cardVariants}
                className="bg-white shadow-md rounded-lg p-6 transform hover:scale-105 transition-transform duration-300"
              >
                <h3 className="text-xl font-semibold text-textPrimary mb-4">
                  {t('donate.projectTitle')}
                </h3>
                <p className="text-textPrimary mb-4">
                  {t('donate.projectDescription')}
                </p>
                <a
                  href="mailto:info@notashamedofthegospelministry.org.com?subject=Project Sponsorship Inquiry"
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
                >
                  {t('donate.projectButton')}
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bank Transfer Section */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible" // Animate when the element comes into view
            viewport={{ once: true, amount: 0.2 }} // Trigger animation once when 20% of the element is visible
            variants={bankTransferVariants}
          >
            <h2 className="text-3xl md:text-4xl font-semibold text-textPrimary text-center mb-6">
              {t('donate.bankTransferTitle')}
            </h2>
            <div className="max-w-lg mx-auto text-center">
              <p className="text-textPrimary">
                <span className="font-semibold">{t('donate.bankLabel')}</span> {t('donate.bankName')}<br />
                <span className="font-semibold">{t('donate.accountNameLabel')}</span> {t('donate.accountName')}<br />
                <span className="font-semibold">{t('donate.accountNumberLabel')}</span> {t('donate.accountNumber')}<br />
                <span className="font-semibold">{t('donate.swiftCodeLabel')}</span> {t('donate.swiftCode')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Donate;